'use client';

import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, ArrowLeft, AlertCircle, Loader2, Building2, Users, Shield, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTenantEnrollmentService } from '@/lib/use-generated-lib';
import type { TenantEnrollmentRequestSsoProviderTypeEnum } from '@/lib/generated-api/models/TenantEnrollmentRequest';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses, getInputClasses } from '@/lib/theme-utils';

interface VerificationProps {
  tenantData: {
    domain: string;
    organizationName: string;
    ssoConfig: any;
    adminEmail: string;
    adminFirstName: string;
    adminLastName: string;
    adminPassword: string;
    settings: {
      timezone: string;
      defaultLanguage: string;
      notificationPreferences: {
        emailAlerts: boolean;
        smsAlerts: boolean;
      };
    };
  };
  onComplete: () => void;
  onBack: () => void;
  isLoading?: boolean;
}

type VerificationStep = 'testing' | 'success' | 'error' | 'consent-required';

export function Verification({ tenantData, onComplete, onBack, isLoading }: VerificationProps) {
  const { theme } = useTheme();
  const [step, setStep] = useState<VerificationStep>('testing');
  const [testResults, setTestResults] = useState<{
    domain: boolean;
    sso: boolean;
    email: boolean;
  }>({
    domain: false,
    sso: false,
    email: false
  });
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [consentUrl, setConsentUrl] = useState<string | null>(null);
  const { tenantEnrollmentService, isLoaded: tenantEnrollmentServiceLoaded } = useTenantEnrollmentService();
  const { refreshSessionAfterEnrollment } = useAuth();
  const router = useRouter();

  useEffect(() => {
    // Simulate verification process
    const runVerification = async () => {
      try {
        // Test domain verification
        await new Promise(resolve => setTimeout(resolve, 1000));
        setTestResults(prev => ({ ...prev, domain: true }));

        // Test authentication configuration
        await new Promise(resolve => setTimeout(resolve, 1500));
        setTestResults(prev => ({ ...prev, sso: true }));

        // Test email verification
        await new Promise(resolve => setTimeout(resolve, 1000));
        setTestResults(prev => ({ ...prev, email: true }));

        setStep('success');
      } catch (error) {
        setError('Verification failed. Please check your configuration and try again.');
        setStep('error');
      }
    };

    runVerification();
  }, []);

  const handleEnrollmentSuccess = async (enrollmentResult: any) => {
    console.log('Organization enrolled successfully:', enrollmentResult);
    
    // Store the tenant ID as preferred if provided
    if (enrollmentResult.tenantId) {
      localStorage.setItem('preferred_tenant_id', enrollmentResult.tenantId);
    }

    // Set a flag to indicate enrollment completion
    sessionStorage.setItem('enrollment_completed', 'true');
    
    // If consent URL is provided (for Enterprise App SSO), show it to the user
    if (enrollmentResult.consentUrl) {
      // Store consent URL for display
      setConsentUrl(enrollmentResult.consentUrl);
      sessionStorage.setItem('pending_consent_url', enrollmentResult.consentUrl);
      // Don't redirect yet - user needs to complete admin consent first
      setStep('consent-required');
      return;
    }
    
    // Refresh the session to get the new tenant access
    try {
      console.log(' Starting session refresh after enrollment...');
      await refreshSessionAfterEnrollment();
      
      console.log(' Session refresh successful, redirecting to dashboard');
      // Redirect to dashboard or main app
      router.push('/dashboard');
      
    } catch (error) {
      console.error('❌ Failed to refresh session after enrollment:', error);
      
      // Fallback: reload the page to trigger fresh auth flow
      console.log(' Falling back to page reload...');
      window.location.href = '/';
    }
  };

  const handleComplete = async () => {
    try {
      setEnrolling(true);
      setError(null);

      if (!tenantEnrollmentService || !tenantEnrollmentServiceLoaded) {
        throw new Error('Enrollment service not ready');
      }

      // Debug authentication token
      console.log('Checking for auth token...');
      
      // Try to get the OIDC user data which contains the access token
      const oidcUserKey = 'oidc.user:https://keycloak-dev.cobalt-technologies.com/realms/multi-tenant-saas:nxus-client-app';
      const oidcUserData = localStorage.getItem(oidcUserKey);
      console.log('OIDC user data found:', !!oidcUserData);
      
      let access_token;
      if (oidcUserData) {
        try {
          const userData = JSON.parse(oidcUserData);
          access_token = userData.access_token;
          console.log('Successfully extracted access_token from OIDC user data:', !!access_token);
        } catch (parseError) {
          console.error('Failed to parse OIDC user data:', parseError);
        }
      }
      
      // Fallback to the original authToken key
      if (!access_token) {
        const authTokenData = localStorage.getItem('authToken');
        console.log('Fallback: Raw auth token data:', authTokenData);
        
        if (authTokenData) {
          try {
            const parsed = JSON.parse(authTokenData);
            access_token = parsed.access_token;
            console.log('Successfully parsed auth token, access_token exists:', !!access_token);
          } catch (parseError) {
            console.error('Failed to parse auth token:', parseError);
          }
        }
      }

      if (!access_token) {
        console.error('No access_token found in any storage location');
        console.log('Available localStorage keys:', Object.keys(localStorage));
        throw new Error('No authentication token available. Please refresh the page and try again.');
      }

      // Map SSO provider type to backend enum
      const getSsoProviderType = (ssoConfig: any): TenantEnrollmentRequestSsoProviderTypeEnum => {
        if (!ssoConfig || ssoConfig.ssoProviderType === 'local') return 'local';
        if (ssoConfig.ssoProviderType === 'azure') return 'azure';
        if (ssoConfig.ssoProviderType === 'okta') return 'okta';
        return 'local';
      };

      // Prepare the enrollment request with camelCase field names (as expected by the API)
      const enrollmentRequest = {
        organizationName: tenantData.organizationName,
        domain: tenantData.domain,
        adminEmail: tenantData.adminEmail,
        adminFirstName: tenantData.adminFirstName,
        adminLastName: tenantData.adminLastName,
        adminPassword: tenantData.adminPassword,
        ssoProviderType: getSsoProviderType(tenantData.ssoConfig),
        providerConfig: tenantData.ssoConfig?.providerConfig || {},
        settings: {
          timezone: tenantData.settings.timezone,
          defaultLanguage: tenantData.settings.defaultLanguage,
          notificationPreferences: {
            emailAlerts: tenantData.settings.notificationPreferences.emailAlerts,
            smsAlerts: tenantData.settings.notificationPreferences.smsAlerts
          }
        }
      };

      console.log('Submitting enrollment request:', enrollmentRequest);
      console.log('TenantEnrollmentService ready:', tenantEnrollmentServiceLoaded);
      console.log('Service instance:', tenantEnrollmentService);
      console.log('Request URL:', 'http://localhost:8080/api/v1/enrollment/tenant');
      console.log('Request method:', 'POST');
      console.log('Request headers:', { 'Content-Type': 'application/json' });

      console.log('About to call enrollTenant API...');
      const startTime = Date.now();
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      try {
        // Call the enrollment API with authentication and timeout
        const result = await tenantEnrollmentService.enrollTenant({
          tenantEnrollmentRequest: enrollmentRequest
        }, {
          headers: {
            'Authorization': `Bearer ${access_token}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        const endTime = Date.now();
        console.log(`Enrollment API call completed in ${endTime - startTime}ms`);
        
        // Handle the enrollment success
        await handleEnrollmentSuccess(result);
        
      } catch (timeoutError) {
        clearTimeout(timeoutId);
        
        if (timeoutError instanceof Error && timeoutError.name === 'AbortError') {
          throw new Error('Request timed out. Please try again.');
        }
        throw timeoutError;
      }
    } catch (error) {
      console.error('Organization enrollment error:', error);
      
      // Log additional error details for debugging
      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as any).response;
        console.error('Response status:', response?.status);
        console.error('Response headers:', response?.headers);
        try {
          const responseText = await response?.text();
          console.error('Response body:', responseText);
        } catch (e) {
          console.error('Could not read response body:', e);
        }
      }
      
      // Provide more specific error messages based on the error type
      let errorMessage = 'Unable to complete enrollment. Please try again.';
      
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        
        // Check for specific error patterns
        if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (errorMsg.includes('server') || errorMsg.includes('500')) {
          errorMessage = 'Server error. Please try again in a moment.';
        } else if (errorMsg.includes('unauthorized') || errorMsg.includes('401')) {
          errorMessage = 'Authentication error. Please refresh and try again.';
        } else if (errorMsg.includes('forbidden') || errorMsg.includes('403')) {
          errorMessage = 'Access denied. The enrollment service may be temporarily unavailable. Please try again or contact support.';
        } else if (errorMsg.includes('not found') || errorMsg.includes('404')) {
          errorMessage = 'Enrollment service not found. Please contact support.';
        } else if (errorMsg.includes('bad request') || errorMsg.includes('400')) {
          errorMessage = 'Invalid request data. Please check your information and try again.';
        } else if (errorMsg.includes('conflict') || errorMsg.includes('409')) {
          errorMessage = 'Organization or domain already exists. Please use different information.';
        } else if (errorMsg.includes('validation') || errorMsg.includes('422')) {
          errorMessage = 'Validation error. Please check your information and try again.';
        } else {
          // For other errors, try to extract more details
          errorMessage = `Enrollment failed: ${error.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setEnrolling(false);
    }
  };

  const getStepIcon = (stepName: keyof typeof testResults) => {
    if (testResults[stepName]) {
      return <CheckCircle className="w-5 h-5 " />;
    }
    if (step === 'testing') {
      return <Loader2 className="w-5 h-5  animate-spin" style={{ color: 'var(--theme-accent)' }} />;
    }
    return <div className={`w-5 h-5 border-2 ${getBorderClasses(theme)} rounded-full`} />;
  };

  const getStepStatus = (stepName: keyof typeof testResults) => {
    if (testResults[stepName]) return 'completed';
    if (step === 'testing') return 'testing';
    return 'pending';
  };

  const isLocalAuth = tenantData.ssoConfig?.ssoProviderType === 'local';

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-3">
          <CheckCircle className="w-6 h-6 text-white" />
        </div>
        <h2 className={`text-xl font-bold ${getTextClasses(theme)} mb-1`}>
          Verify Your Setup
        </h2>
        <p className={`${getTextClasses(theme, 'muted')} text-sm`}>
          Review your organization configuration before completing the setup
        </p>
      </div>

      {/* Configuration Summary */}
      <div className="space-y-4 mb-6">
        {/* Organization Info */}
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-4`}>
          <h3 className={`text-sm font-semibold ${getTextClasses(theme)} mb-3 flex items-center`}>
            <Building2 className="w-4 h-4 mr-2 " />
            Organization Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <span className={getTextClasses(theme, 'muted')}>Organization:</span>
              <p className={`${getTextClasses(theme)} font-medium`}>{tenantData.organizationName}</p>
            </div>
            <div>
              <span className={getTextClasses(theme, 'muted')}>Domain:</span>
              <p className={`${getTextClasses(theme)} font-medium`}>{tenantData.domain}</p>
            </div>
          </div>
        </div>

        {/* Admin Info */}
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-4`}>
          <h3 className={`text-sm font-semibold ${getTextClasses(theme)} mb-3 flex items-center`}>
            <Users className="w-4 h-4 mr-2" style={{ color: 'var(--theme-info)' }} />
            Administrator Account
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <span className={getTextClasses(theme, 'muted')}>Name:</span>
              <p className={`${getTextClasses(theme)} font-medium`}>{tenantData.adminFirstName} {tenantData.adminLastName}</p>
            </div>
            <div>
              <span className={getTextClasses(theme, 'muted')}>Email:</span>
              <p className={`${getTextClasses(theme)} font-medium`}>{tenantData.adminEmail}</p>
            </div>
          </div>
        </div>

        {/* Authentication Info */}
        <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-4`}>
          <h3 className={`text-sm font-semibold ${getTextClasses(theme)} mb-3 flex items-center`}>
            <Shield className="w-4 h-4 mr-2 " />
            Authentication Method
          </h3>
          <div className="text-xs">
            <span className={getTextClasses(theme, 'muted')}>Type:</span>
            <p className={`${getTextClasses(theme)} font-medium capitalize`}>
              {tenantData.ssoConfig?.ssoProviderType === 'local' ? 'Local Authentication' : 
               `${tenantData.ssoConfig?.ssoProviderType} SSO`}
            </p>
            {tenantData.ssoConfig?.ssoProviderType !== 'local' && (
              <div className="mt-2">
                <span className={getTextClasses(theme, 'muted')}>Provider:</span>
                <p className={`${getTextClasses(theme)} font-medium`}>{tenantData.ssoConfig?.providerConfig?.clientId || 'Not configured'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Consent URL Display */}
      {step === 'consent-required' && consentUrl && (
        <div className="/20 border border-blue-700 rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--theme-info)' }}>
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5  mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold  mb-2" style={{ color: 'var(--theme-info)' }}>
                Admin Consent Required
              </h3>
              <p className="text-xs  mb-3" style={{ color: 'var(--theme-info)' }}>
                Your organization's Azure AD administrator must grant consent to the Enterprise Application before SSO can be used. 
                Please share this URL with your Azure AD administrator or complete it yourself if you have admin permissions.
              </p>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="text"
                  value={consentUrl}
                  readOnly
                  className={`flex-1 ${getInputClasses(theme)} text-xs font-mono p-2 rounded`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(consentUrl);
                  }}
                  className="flex-shrink-0 text-xs"
                >
                  Copy
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    window.open(consentUrl, '_blank', 'noopener,noreferrer');
                  }}
                  className="flex-shrink-0 text-xs"
                >
                  Open
                </Button>
              </div>
              <p className="text-xs /70" style={{ color: 'var(--theme-info)' }}>
                <strong>Note:</strong> After completing admin consent, you can continue with the setup. 
                SSO will be fully configured once consent is granted.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="/10 border /30 rounded-lg p-3 mb-4" style={{ backgroundColor: 'var(--theme-error)' }}>
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4  mr-2" />
            <p className="text-xs ">{error}</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-4">
        <Button
          type="button"
          onClick={onBack}
          variant="outline"
          disabled={enrolling}
          className={`flex-1 ${getBackgroundClasses(theme, 'default')} ${getBorderClasses(theme)} ${getTextClasses(theme)} h-10`}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {step === 'consent-required' ? (
          <Button
            onClick={async () => {
              // After consent, continue with session refresh
              try {
                await refreshSessionAfterEnrollment();
                router.push('/dashboard');
              } catch (error) {
                console.error('Failed to refresh session:', error);
                window.location.href = '/';
              }
            }}
            className="flex-1  hover:from-blue-600 hover:to-blue-700 text-white h-10"
          >
            Continue Setup
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={handleComplete}
            disabled={isLoading || enrolling}
            className="flex-1  hover:from-green-600 hover:to-green-700 text-white h-10"
          >
            {enrolling ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Setting Up...
              </>
            ) : (
              <>
                Complete Setup
                <ArrowRight className="ml-2 w-4 h-4" />
              </>
            )}
          </Button>
        )}
      </div>

      {/* Help Text */}
      <div className={`mt-4 p-3 ${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg`}>
        <h4 className={`text-xs font-medium ${getTextClasses(theme)} mb-1`}>What happens next?</h4>
        <ul className={`text-xs ${getTextClasses(theme, 'muted')} space-y-0.5`}>
          <li>• Your organization will be created and configured</li>
          <li>• Your session will be refreshed with tenant access</li>
          <li>• You'll be redirected to your new workspace</li>
          <li>• Additional users can be added through the admin panel</li>
        </ul>
      </div>
    </div>
  );
}